import { Direction } from '@angular/cdk/bidi';
import {
  Component,
  DOCUMENT,
  HostListener,
  inject,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { IgxOverlayOutletDirective } from '@infragistics/igniteui-angular';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { AuthenticationService, ConfigurationService } from 'app/shared';
import { NeedsToUnsubscribe } from 'app/shared/core';
import { registerDynamicLocale } from 'app/shared/core/register-locale';
import { LoadUserInfo } from 'app/state/actions/user-info.actions';
import { AppState } from 'app/state/app.state';
import { ShowTranslationKeyService } from 'app/transloco/custom-fallback-strategy';
import { filter, takeUntil } from 'rxjs/operators';
import { ThemeName, ThemeService } from './library/shared/theme-service';
import { ApplicationInsightService } from './shared/errors/application-insights.service';
import { HubSpotService } from './shared/services/hubspot.service';
import { IgxGlobalOverlayService } from './shared/services/igx-global-overlay.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent extends NeedsToUnsubscribe implements OnInit {
  router = inject(Router);
  private configService = inject(ConfigurationService);
  private translate = inject(TranslocoService);
  private authService = inject(AuthenticationService);
  private store = inject<Store<AppState>>(Store);
  private renderer = inject(Renderer2);
  private igxGlobalOverlay = inject(IgxGlobalOverlayService);
  private showTranslationKeyService = inject(ShowTranslationKeyService);
  private _ = inject(HubSpotService);
  private _document = inject<Document>(DOCUMENT);

  private currentUserLanguage: string;
  private userInfo$ = this.store.select((state) => state.userInfo);
  private keysPressed = [];
  buildInfo = '';
  buildInfoVisible = false;
  loadingCurrent = false;
  currentThemeIdx = 0;
  dir: Direction = 'ltr';

  @HostListener('document:keydown', ['$event']) handleKeydown(event: KeyboardEvent) {
    this.keysPressed.push(event.key);
    this.keysPressed = this.keysPressed.slice(-3);
    switch (this.keysPressed.join(';')) {
      case 'Escape;Escape;Escape':
        this.toggleBuildinfo();
        break;
      case 'Escape;Escape;1':
        this.toggleTheme('petrol');
        break;
      case 'Escape;Escape;2':
        this.toggleTheme('lavender');
        break;
      case 'Escape;Escape;0':
        this.resetTheme();
        break;
      case 'Escape;Escape;t':
        this.showTranslationKeyService.toggleTranslationKeys();
        break;
      default:
        break;
    }
  }

  @ViewChild(IgxOverlayOutletDirective) set overlay(value: IgxOverlayOutletDirective) {
    this.igxGlobalOverlay.register(value);
  }
  themeService = inject(ThemeService);
  constructor() {
    const applicationInsights = inject(ApplicationInsightService);

    super();
    applicationInsights.trackPageView();
  }
  ngOnInit(): void {
    this.translate.setDefaultLang('en');

    this.authService.userLoadededEvent.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
      if (this.authService.loggedIn && this.currentUserLanguage == null && !this.loadingCurrent) {
        this.loadingCurrent = true;
        this.store.dispatch(new LoadUserInfo());
      }
    });

    this.userInfo$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((u) => u !== null),
      )
      .subscribe((user) => {
        if (user.userId != null) {
          this.currentUserLanguage = user.languageCode;
          registerDynamicLocale(user.displayLanguageCode);
          this.setI18NAttributes(user.languageCode, user.rtl ? 'rtl' : 'ltr');
          if (user.status < 1 || !user.companyActive) {
            this.authService.startSignoutMainWindow();
          }
          this.loadingCurrent = false;
        }
      });

    this.store
      .select((s) => s.surveyState)
      .subscribe((surveyState) => {
        this.setI18NAttributes(surveyState.languageCode, surveyState.languageDir);
      });
  }

  private setI18NAttributes(lang: string, dir: Direction) {
    this.renderer.setAttribute(this._document.documentElement, 'lang', lang);
    this.dir = dir;
    this.renderer.setAttribute(this._document.documentElement, 'dir', this.dir);
  }
  toggleTheme(theme: ThemeName) {
    this.themeService.setTheme(theme);
  }

  resetTheme() {
    this.themeService.resetToStoreTheme();
  }

  toggleBuildinfo() {
    const config = this.configService.configuration;
    this.buildInfo =
      config.BuildName === '!Local'
        ? 'Local build'
        : `
                ${config.BuildName}, Build: ${config.BuildVersion}
                 source branch [${config.BuildBranch}]
                `;
    this.buildInfoVisible = !this.buildInfoVisible;
    this.keysPressed = [];
  }
}
